import axios from 'axios';

// Resolve the API base URL with three layers of precedence:
//   1. REACT_APP_API_URL  → explicit override (recommended for production builds)
//   2. NODE_ENV === 'development'  → talk to the LOCAL backend (CRA's `npm start`)
//   3. Fallback to the deployed production API
//
// This fixes the case where the frontend was silently talking to production
// while the developer was running everything locally.
const isLocalHost =
  typeof window !== 'undefined' &&
  ['localhost', '127.0.0.1', '0.0.0.0', ''].includes(window.location?.hostname || '');

const RESOLVED_BASE_URL =
  process.env.REACT_APP_API_URL ||
  (process.env.NODE_ENV === 'development' || isLocalHost
    ? 'http://localhost:5001/api'
    : 'https://api.invoice.veramasa.com/api');

// Tiny console hint so you can verify which backend the app is hitting.
// Only logs once, only in dev mode.
if (process.env.NODE_ENV === 'development') {
  // eslint-disable-next-line no-console
  console.info('[api] base URL =', RESOLVED_BASE_URL);
}

const API = axios.create({ baseURL: RESOLVED_BASE_URL });

API.interceptors.request.use((config) => {
  const token = localStorage.getItem('token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

API.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      window.location.href = '/login';
    }
    return Promise.reject(error);
  }
);

export const login = (data) => API.post('/auth/login', data);
export const getClients = () => API.get('/clients');
export const getClient = (id) => API.get(`/clients/${id}`);
export const addClient = (data) => API.post('/clients', data);
export const getInvoices = () => API.get('/invoices');
export const getInvoice = (id) => API.get(`/invoices/${id}`);
export const createInvoice = (data) => API.post('/invoices', data);
export const updateInvoice = (id, data) => API.put(`/invoices/${id}`, data);
export const deleteInvoice = (id) => API.delete(`/invoices/${id}`);
// ── Invoice cancellation (soft-cancel; CEO/HR only) ─────────────────
export const cancelInvoice         = (id, data) => API.put(`/invoices/${id}/cancel`, data);
export const getCancelledInvoices  = ()         => API.get('/invoices/list/cancelled');
export const updateInvoiceRemark   = (id, data) => API.put(`/invoices/${id}/remark`, data);

// ── GST verification ───────────────────────────────────
export const verifyGstin           = (gstin)    => API.post('/gst/verify', { gstin });
export const updateClient = (id, data) => API.put(`/clients/${id}`, data);
export const deleteClient = (id) => API.delete(`/clients/${id}`);
export const getSectorConfig = (sector) => API.get(`/invoices/config/sector/${sector}`);

// ── CRM ────────────────────────────────────────────────
// Leads
export const getCRMLeads       = ()          => API.get('/crm/leads');
export const getCRMLead        = (id)        => API.get(`/crm/leads/${id}`);
export const createCRMLead     = (data)      => API.post('/crm/leads', data);
export const updateCRMLead     = (id, data)  => API.put(`/crm/leads/${id}`, data);
export const deleteCRMLead     = (id)        => API.delete(`/crm/leads/${id}`);

// Deals
export const getCRMDeals       = ()          => API.get('/crm/deals');
export const createCRMDeal     = (data)      => API.post('/crm/deals', data);
export const updateCRMDeal     = (id, data)  => API.put(`/crm/deals/${id}`, data);
export const deleteCRMDeal     = (id)        => API.delete(`/crm/deals/${id}`);

// Tasks
export const getCRMTasks       = ()          => API.get('/crm/tasks');
export const createCRMTask     = (data)      => API.post('/crm/tasks', data);
export const updateCRMTask     = (id, data)  => API.put(`/crm/tasks/${id}`, data);
export const deleteCRMTask     = (id)        => API.delete(`/crm/tasks/${id}`);

// Activities
export const getCRMActivities  = (leadId)    => API.get('/crm/activities', { params: { lead_id: leadId } });
export const createCRMActivity = (data)      => API.post('/crm/activities', data);

// Stats
export const getCRMStats       = ()          => API.get('/crm/stats');

// Lead reassignment + deal handoff
export const reassignCRMLead   = (id, data)  => API.post(`/crm/leads/${id}/reassign`, data);
export const handoffCRMDeal    = (id, data)  => API.post(`/crm/deals/${id}/handoff`, data);

// ── Contracts ──────────────────────────────────────────
export const getContracts      = ()          => API.get('/contracts');
export const getContract       = (id)        => API.get(`/contracts/${id}`);
export const getRenewals       = (days = 60) => API.get('/contracts/renewals', { params: { days } });
export const createContract    = (data)      => API.post('/contracts', data);
export const updateContract    = (id, data)  => API.put(`/contracts/${id}`, data);
export const deleteContract    = (id)        => API.delete(`/contracts/${id}`);
export const getContractDocument = (id)      => API.get(`/contracts/${id}/document`, { responseType: 'blob' });

// ── Proposals ──────────────────────────────────────────
export const getProposals      = ()          => API.get('/proposals');
export const getProposal       = (id)        => API.get(`/proposals/${id}`);
export const createProposal    = (data)      => API.post('/proposals', data);
export const updateProposal    = (id, data)  => API.put(`/proposals/${id}`, data);
export const deleteProposal    = (id)        => API.delete(`/proposals/${id}`);

// ── Users (HR/CEO) + directory for everyone ─────────────
export const getUserDirectory  = ()          => API.get('/users/directory');
export const getMyProfile      = ()          => API.get('/users/me');
export const getUsers          = ()          => API.get('/users');
export const getUser           = (id)        => API.get(`/users/${id}`);
export const createUser        = (data)      => API.post('/users', data);
export const updateUser        = (id, data)  => API.put(`/users/${id}`, data);
export const deactivateUser    = (id)        => API.delete(`/users/${id}`);

// ── Sales Targets ──────────────────────────────────────
export const getTargets        = (userId)    => API.get('/targets', { params: userId ? { user_id: userId } : {} });
export const createTarget      = (data)      => API.post('/targets', data);
export const updateTarget      = (id, data)  => API.put(`/targets/${id}`, data);
export const deleteTarget      = (id)        => API.delete(`/targets/${id}`);

// ── App Settings ────────────────────────────────────────
export const getSettings    = ()     => API.get('/settings');
export const updateSettings = (data) => API.put('/settings', data);

export default API;
